import {
  HOV_SKILL_DEFINITIONS,
  canAffordHovSkillCost,
  getHovSkillDefinition,
  isHovSkillId,
  type HovSkillCost,
  type HovSkillId,
  type HovSkillResourceBalances,
} from "./hovSkillDefinitions";
import {
  EMPTY_HOV_SKILL_EFFECT_MODIFIERS,
  resolveHovSkillExternalMultiplier,
  type HovSkillEffectModifier,
} from "./hovSkillModifiers";
import type { HugeNumber } from "@/shared/numbers";

export interface HovSkillSnapshotEntry {
  readonly id: HovSkillId;
  readonly row: number;
  readonly column: number;
  readonly externalMultiplier: HugeNumber;
}

export interface HovSkillSnapshot {
  readonly acquired: ReadonlySet<HovSkillId>;
  readonly entries: readonly HovSkillSnapshotEntry[];
}

export interface HovSkillState {
  readonly acquiredIds: readonly HovSkillId[];
}

export type HovSkillAcquireFailureReason =
  | "already-acquired"
  | "prerequisites-not-met"
  | "insufficient-resource";

export type HovSkillAcquireResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: HovSkillAcquireFailureReason;
    };

export const emptyHovSkillState: HovSkillState = {
  acquiredIds: [],
};

export type HovSkillCostSpender = (cost: HovSkillCost) => boolean;

export class HovSkillModel {
  private readonly acquired = new Set<HovSkillId>();

  isAcquired(id: HovSkillId): boolean {
    return this.acquired.has(id);
  }

  arePrerequisitesMet(id: HovSkillId): boolean {
    const def = getHovSkillDefinition(id);
    for (const req of def.prerequisites) {
      if (!this.acquired.has(req)) return false;
    }
    return true;
  }

  canAcquire(id: HovSkillId, resources: HovSkillResourceBalances): boolean {
    return this.getAcquireFailureReason(id, resources) === null;
  }

  purchase(
    id: HovSkillId,
    resources: HovSkillResourceBalances,
    spendCost: HovSkillCostSpender,
  ): HovSkillAcquireResult {
    const failureReason = this.getAcquireFailureReason(id, resources);
    if (failureReason !== null) return { ok: false, reason: failureReason };

    const def = getHovSkillDefinition(id);
    if (!spendCost(def.cost)) {
      return { ok: false, reason: "insufficient-resource" };
    }

    this.acquired.add(id);
    return { ok: true };
  }

  getSnapshot(
    externalModifiers: readonly HovSkillEffectModifier[] = EMPTY_HOV_SKILL_EFFECT_MODIFIERS,
  ): HovSkillSnapshot {
    return {
      acquired: new Set(this.acquired),
      entries: HOV_SKILL_DEFINITIONS.map((definition) => ({
        id: definition.id,
        row: definition.row,
        column: definition.column,
        externalMultiplier: resolveHovSkillExternalMultiplier(
          definition.id,
          externalModifiers,
        ),
      })),
    };
  }

  exportState(): HovSkillState {
    return {
      acquiredIds: [...this.acquired],
    };
  }

  restoreState(state: HovSkillState): void {
    this.acquired.clear();
    for (const id of state.acquiredIds) {
      if (!isHovSkillId(id)) {
        throw new Error(`unknown hov skill id: ${String(id)}`);
      }
      this.acquired.add(id);
    }
  }

  static listDefinitions() {
    return HOV_SKILL_DEFINITIONS;
  }

  private getAcquireFailureReason(
    id: HovSkillId,
    resources: HovSkillResourceBalances,
  ): HovSkillAcquireFailureReason | null {
    if (this.isAcquired(id)) {
      return "already-acquired";
    }
    if (!this.arePrerequisitesMet(id)) {
      return "prerequisites-not-met";
    }
    if (!canAffordHovSkillCost(getHovSkillDefinition(id).cost, resources)) {
      return "insufficient-resource";
    }
    return null;
  }
}
